import Nav from "@/components/thoratech/Nav";
import Footer from "@/components/thoratech/Footer";
import Seo from "@/seo/Seo";
import { BRAND } from "@/content/thoratech";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Who we are",
    body: (
      <>
        <p>
          <strong>{BRAND.legal}</strong> (&quot;Thoratech&quot;, &quot;we&quot;, &quot;our&quot;,
          &quot;us&quot;) is a CX and BPO managed-operations company headquartered in {BRAND.city}.
          Our website is published at{" "}
          <a href={`https://${BRAND.website}`} className="text-[#2AC0A5] hover:underline">
            {BRAND.website}
          </a>
          .
        </p>
        <p>
          For any privacy-related question or to exercise your rights described below, contact us at{" "}
          <a href={`mailto:${BRAND.contact.email}`} className="text-[#2AC0A5] hover:underline">
            {BRAND.contact.email}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    h: "2. What this policy covers",
    body: (
      <>
        <p>
          This policy describes how we collect, use, store and share personal information when you
          visit our website or get in touch with us. It is written to be consistent with:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>India&apos;s <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong></li>
          <li>The <strong>EU General Data Protection Regulation (GDPR)</strong> for visitors based in the EU/EEA and UK</li>
          <li>The Information Technology Act, 2000 and its rules, as applicable in India</li>
        </ul>
      </>
    ),
  },
  {
    h: "3. What we collect",
    body: (
      <>
        <p>We only collect the minimum personal data needed to respond to you:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li><strong>Contact details</strong> you provide voluntarily: name, company, email, phone and the message you write to us through the contact or careers form.</li>
          <li><strong>Resume / CV</strong> only if you choose to attach one when applying for a role.</li>
          <li><strong>Technical data</strong> from your browser (IP-derived city, device, browser, pages visited, referrer) collected by analytics tools used by this website.</li>
        </ul>
        <p className="mt-3">
          We do not knowingly collect data from children under 18 and we do not run profile-based
          advertising on this site.
        </p>
      </>
    ),
  },
  {
    h: "4. Why we use your data (legal basis)",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>To respond to your enquiry</strong>, based on the consent you give by submitting the contact form.</li>
          <li><strong>To assess job applications</strong>, based on consent and the steps necessary to enter into a contract of employment.</li>
          <li><strong>To run the website securely</strong>, based on our legitimate interest in operating, securing and improving the site.</li>
          <li><strong>To meet legal obligations</strong>, for example, records we are required to keep under Indian law.</li>
        </ul>
      </>
    ),
  },
  {
    h: "5. Cookies & analytics",
    body: (
      <>
        <p>
          This site uses a small number of cookies and similar technologies to keep the site
          working and to understand how it is used. They fall into three buckets:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li><strong>Strictly necessary cookies</strong>, required for the site to function (e.g. remembering your cookie choice). These cannot be turned off.</li>
          <li><strong>Analytics cookies</strong>, let us count visits and traffic sources so we can measure and improve performance. Anonymised wherever possible.</li>
          <li><strong>Functional cookies</strong>, remember choices you make to give you a better experience.</li>
        </ul>
        <p className="mt-3">
          When you first visit the site, our cookie banner lets you accept or reject non-essential
          cookies. You can change your choice at any time by clearing your browser storage for this
          site.
        </p>
      </>
    ),
  },
  {
    h: "6. Who we share data with",
    body: (
      <>
        <p>
          We do not sell your personal data. We only share it with a limited set of trusted parties:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>Cloud, email, analytics and infrastructure providers that help us operate the website and respond to you. They are bound by appropriate confidentiality and data-protection obligations.</li>
          <li>Professional advisers (legal, accounting) and government or regulatory authorities, where required by law.</li>
        </ul>
        <p className="mt-3">
          Some of these providers may process data outside India. Where this happens, we rely on
          appropriate safeguards consistent with applicable law, including, where relevant, the
          standard contractual clauses required under the GDPR.
        </p>
      </>
    ),
  },
  {
    h: "7. How long we keep your data",
    body: (
      <p>
        We keep personal data only for as long as needed for the purposes described above and to
        meet legal, tax and accounting requirements. Contact-form enquiries are typically retained
        for up to 24 months. Job-application data is typically retained for up to 12 months from
        the date of submission unless you ask us to delete it earlier.
      </p>
    ),
  },
  {
    h: "8. Your rights",
    body: (
      <>
        <p>
          Subject to the DPDP Act, the GDPR and other applicable laws, you have the right to:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>Access the personal data we hold about you</li>
          <li>Ask us to correct or update inaccurate data</li>
          <li>Ask us to erase your data (&quot;right to be forgotten&quot;)</li>
          <li>Withdraw consent you previously gave</li>
          <li>Object to or restrict certain processing</li>
          <li>Request portability of data you provided to us</li>
          <li>Nominate another individual to exercise these rights on your behalf in case of death or incapacity (DPDP Act)</li>
          <li>Lodge a complaint with the Data Protection Board of India or, where applicable, your local EU supervisory authority</li>
        </ul>
        <p className="mt-3">
          To exercise any of these rights, write to us at{" "}
          <a href={`mailto:${BRAND.contact.email}`} className="text-[#2AC0A5] hover:underline">
            {BRAND.contact.email}
          </a>
          . We respond within 30 days.
        </p>
      </>
    ),
  },
  {
    h: "9. Security",
    body: (
      <p>
        We apply reasonable technical and organisational measures to protect personal data,
        including access controls, encrypted transport and a clear internal data-handling policy.
        No system is perfectly secure, if you believe your data has been compromised, please
        contact us immediately.
      </p>
    ),
  },
  {
    h: "10. Updates to this policy",
    body: (
      <p>
        We may update this policy from time to time as our operations or applicable laws evolve.
        The latest version will always be posted on this page with a revised effective date.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <>
      <Nav />
      <Seo route="/privacy" />
      <main data-testid="privacy-page" className="bg-white pt-[110px] md:pt-[140px] pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="flex w-fit items-center gap-2 text-sm font-medium text-[#01080E]/70 hover:text-[#2AC0A5] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>

          <div className="inline-flex items-center gap-3 mb-6">
            <span className="block h-px w-8 bg-[#2AC0A5]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
              Legal · Privacy
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.05] text-[#01080E]">
            Privacy Policy
          </h1>
          <p className="mt-5 text-[#01080E]/60 text-sm">
            Effective date: December 2025 · Reviewed in line with India&apos;s DPDP Act, 2023 and the EU GDPR.
          </p>

          <div className="mt-12 space-y-12">
            {SECTIONS.map((s, i) => (
              <section key={i} className="border-t border-[#01080E]/10 pt-8">
                <h2 className="font-display text-2xl font-bold text-[#01080E]">{s.h}</h2>
                <div className="mt-4 text-[#01080E]/80 text-[15px] leading-relaxed space-y-3">
                  {s.body}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
